import {Component, computed, input, OnInit} from '@angular/core';
import {Button} from 'primeng/button';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {FormsModule} from '@angular/forms';
import {AutoComplete} from 'primeng/autocomplete';
import {DatePipe} from '@angular/common';
import {TableModule} from 'primeng/table';
import {ApprovalContext, BasicTaDetails, BasicUserDetails, SubmissionApproversDetails, UserType} from '../../../../api';
import {TaAppStore} from '../../../../state/clcta-store';
import {ApprovalWorkflowHistoryStage} from '../../../../util/app.types';
import {toApprovalWorkflowHistory} from '../../../../util/approval-workflow.utils';
import {Badge} from 'primeng/badge';

export interface ManageApproverRow extends ApprovalWorkflowHistoryStage {
  newApprover: BasicUserDetails | null;
}

// One of the two dates an approval window runs from, shown under the modal title.
interface ApprovalTimelineLine {
  label: string;
  date: string;
  overdueAt?: string | null;
}

@Component({
  selector: 'manage-approvers-modal',
  imports: [
    Button,
    FormsModule,
    AutoComplete,
    DatePipe,
    TableModule,
    Badge,
  ],
  templateUrl: './manage-approvers-modal.component.html',
  styleUrl: './manage-approvers-modal.component.scss'
})
export class ManageApproversModalComponent implements OnInit {
  modalData = input<BasicTaDetails>();
  availableApprovers = input<BasicUserDetails[]>([]);

  // Casual staff never hold an approval, so they are not offered as a replacement approver.
  approverOptions = computed<BasicUserDetails[]>(() =>
    this.availableApprovers().filter(user => user.userType !== UserType.Casual)
  );

  approverRows: ManageApproverRow[] = [];
  // At most two lines: when the TA was submitted, and when its acquittal was completed.
  timeline: ApprovalTimelineLine[] = [];
  // Shared by every row's autocomplete - only one row can be open at a time.
  filteredApprovers: BasicUserDetails[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public store: TaAppStore
  ) {}

  ngOnInit() {
    // Every approval the TA has held, so superseded ones stay visible as history.
    this.approverRows = toApprovalWorkflowHistory(this.modalData()?.approvals)
      .map(stage => ({...stage, newApprover: null}));

    // The travel stages share one line and the acquittal stages (manager, then funding) the other,
    // so an overdue Funding or CEO approval still surfaces against the submission date. Cancellation
    // gets its own line - it sits outside the chain, so its overdue date must not read as the submission's.
    const isAcquittalRow = (row: ManageApproverRow) =>
      row.context === ApprovalContext.Acquittal || row.context === ApprovalContext.SecondaryAcquittal;
    const travelRows = this.approverRows.filter(row =>
      !isAcquittalRow(row) && row.context !== ApprovalContext.Cancellation);
    const acquittalRows = this.approverRows.filter(isAcquittalRow);
    // One Acquittal row per acquittal round; a TA with no manager acquittal approval has only funding rows.
    const managerAcquittalRows = acquittalRows.filter(row => row.context === ApprovalContext.Acquittal);
    const cancellationRows = this.approverRows.filter(row => row.context === ApprovalContext.Cancellation);
    this.timeline = [
      this.toTimelineLine('Submitted for Approval', 'Resubmitted for Approval',
        travelRows.filter(row => row.context === ApprovalContext.Manager), travelRows),
      this.toTimelineLine('Acquittal Complete', 'Reacquitted',
        managerAcquittalRows.length ? managerAcquittalRows : acquittalRows, acquittalRows),
      this.toCancellationTimelineLine(cancellationRows),
    ].filter((line): line is ApprovalTimelineLine => !!line);
  }

  /**
   * The cancellation line, or null for a TA with no cancellation request. Its rows are parallel
   * rather than sequential - one per approver, requested together and never resubmitted - so they
   * share one line anchored on the earliest, the same anchor the server's staleness rule uses.
   */
  private toCancellationTimelineLine(rows: ManageApproverRow[]): ApprovalTimelineLine | null {
    const created = rows[0]?.approval.created;
    if (!created) {
      return null;
    }

    return {
      label: 'Cancellation Requested',
      date: created,
      overdueAt: rows.find(row => row.approval.overdueDate)?.approval.overdueDate,
    };
  }

  /**
   * A line for a stage the TA has reached, or null for one it has not. Withholding deletes the
   * remaining pending approvals and resubmitting regenerates them, so a second row for the same
   * context means the TA came back around - and its window runs from that newest round.
   */
  private toTimelineLine(label: string, resubmittedLabel: string,
                         dateRows: ManageApproverRow[], stageRows: ManageApproverRow[]): ApprovalTimelineLine | null {
    const created = dateRows.at(-1)?.approval.created;
    if (!created) {
      return null;
    }

    return {
      label: dateRows.length > 1 ? resubmittedLabel : label,
      date: created,
      // Only ever one approval is flagged, and the whole stage shares the one line.
      overdueAt: stageRows.find(row => row.approval.overdueDate)?.approval.overdueDate,
    };
  }

  /**
   * Only the newest approval of a lifecycle step can be overridden - the server keys a chain-stage
   * override by context and rejects a request holding two overrides for the same one. Cancellation
   * rows are parallel and keyed by approvalId instead, so every pending one is editable.
   */
  isEditable(row: ManageApproverRow): boolean {
    return row.isLatest && !row.actionedDate;
  }

  /** The row's own approver is never offered - replacing them with themselves is a no-op. */
  filterApprovers(event: any, row: ManageApproverRow): void {
    const query = event.query.toLowerCase();
    const currentApproverId = row.approval.approver?.id;
    this.filteredApprovers = this.approverOptions().filter(user =>
      (!currentApproverId || user.id !== currentApproverId) &&
      user.name?.toLowerCase().includes(query)
    );
  }

  clearNewApprover(row: ManageApproverRow): void {
    row.newApprover = null;
  }

  hasChanges(): boolean {
    return this.approverRows.some(row => !!row.newApprover);
  }

  /**
   * One slot per lifecycle step - superseded rows are history and are never sent - with
   * newApproverId set only on the rows that were changed.
   */
  toOverrideDetails(): SubmissionApproversDetails {
    const data = this.modalData();
    return {
      submissionId: data?.id ?? undefined,
      taNumber: data?.taNumber,
      primaryTravellerName: data?.primaryTraveller?.details?.name,
      travelDate: data?.startDate,
      approvals: this.approverRows.filter(row => row.isLatest).map(row => ({
        context: row.context,
        approvalId: row.approval.id ?? null,
        status: row.approval.status,
        action: row.approval.actionType,
        actionedDate: row.actionedDate,
        currentApprover: row.approval.approver,
        groupName: row.approval.groupApprovers?.length ? row.approval.displayName : null,
        newApproverId: row.newApprover?.id ?? null,
      })),
    };
  }

  saveDialog(): void {
    this.ref.close(this.toOverrideDetails());
  }

  closeDialog(): void {
    this.ref.close();
  }
}
